import { mergeGraph, registerGraph } from '@/angular/modules/vrm-graph/config-utils';
import i18n from '@/vue/languages';


registerGraph('essAlgorithmGraph', essAlgorithmGraph);

/* @ngInject */
function essAlgorithmGraph(GraphService, EssGraphService, locale, defaultGraph, nowIndicatorConfig) {

    const colors = {
        transparent: {
            label: 'transparent',
            value: EssGraphService.colors.TRANSPARENT
        },
        solarToConsumption: {
            label: 'lemon-green',
            value: EssGraphService.colors.LEMON_GREEN
        },
        batteryToConsumption: {
            label: 'blue',
            value: EssGraphService.colors.BLUE
        },
        gridToConsumption: {
            label: 'light-pink',
            value: EssGraphService.colors.LIGHT_PINK
        },
        solarToGrid: {
            label: 'orange',
            value: EssGraphService.colors.ORANGE
        },
        solarToBattery: {
            label: 'green',
            value: EssGraphService.colors.GREEN
        },
        batteryToGrid: {
            label: 'cyan',
            value: EssGraphService.colors.CYAN
        },
        gridToBattery: {
            label: 'magenta',
            value: EssGraphService.colors.MAGENTA
        }
    };

    return mergeGraph(defaultGraph(), nowIndicatorConfig(), {
        extra: {
            legendItems: [
                'battery_to_grid',
                'solar_to_grid',
                'solar_to_battery',
                'solar_to_consumption',
                'battery_to_consumption',
                'grid_to_consumption',
                'grid_to_battery',
            ]
        },
        size: {
            height: null // fix to make it scale to container height
        },
        options: {
            chart: {
                plotBackgroundColor: '#f0efeb',
                marginBottom: 70, // to make space for longer time labels i.e. '17. Aug 02:00'
                marginLeft: 48, // to have the width of the left axis panel fixed
                pointWidth: 40,
            },
            plotOptions: {
                column: {
                    pointPadding: 0.03,
                    groupPadding: 0,
                    pointRange: 3600 * 1000,
                    // borderWidth: 1,
                    // borderColor: 'rgba(0,0,0,0)',
                    grouping: true,
                    crisp: false,
                    pointPlacement: 'on',
                    stacking: 'normal',
                },
                line: {
                    marker: {
                        enabled: false
                    },
                    states: { hover: { enabled: false } },
                    connectNulls: false,
                    connectEnds: true
                },
            },
            tooltip: EssGraphService.getTooltipFormatter(GraphService, 'kWh', ['total_consumption', 'total_solar', 'total_consumption_fc', 'total_solar_fc']),
        },
        chart: {
            type: 'column',
            styledMode: true
        },
        xAxis: {
            type: 'datetime',
            labels: { formatter: GraphService.datetimeLabeler() },
            startOfWeek: 1,
            tickInterval: 1000 * 60 * 60,
            offset: 0,
            lineWidth: 0,
            minPadding: 0,
            maxPadding: 0,
            plotLines: [
                {
                    color: GraphService.colors.lightGrey,
                    value: null,
                    width: 1,
                    zIndex: 4
                }
            ]
        },
        yAxis: [
            {
                title: {
                    text: i18n.t('graph.kwh', { i18nFallback: 'kWh' }),
                    align: 'high',
                    y: -30,
                    rotation: 0,
                    offset: 0
                },
                plotLines: [
                    {
                        color: GraphService.colors.lightGrey,
                        value: 0,
                        width: 1,
                    }
                ],
                labels: {
                    formatter: function () {
                        const value = Math.abs(this.value);
                        return Number.isInteger(value) ? value : value.toFixed(2);
                    }
                },
                gridLineWidth: 0,
                visible: true,
                showFirstLabel: true,
                reversedStacks: false
            },
        ],
        series: [
            {
                name: i18n.t('graph.battery_to_grid', { i18nFallback: 'Battery to Grid' }),
                type: "column",
                pointPlacement: "between",
                id: "battery_to_grid",
                color: EssGraphService.getColumnColor(colors.batteryToGrid.value),
                borderColor: EssGraphService.getColumnColor(colors.batteryToGrid.value),
                colorName: colors.batteryToGrid.label,
                visible: false,
                index: 10,
                data: []
            },
            {
                name: i18n.t('graph.solar_to_grid', { i18nFallback: 'Solar to Grid' }),
                type: "column",
                pointPlacement: "between",
                id: "solar_to_grid",
                color: EssGraphService.getColumnColor(colors.solarToGrid.value),
                borderColor: EssGraphService.getColumnColor(colors.solarToGrid.value),
                colorName: colors.solarToGrid.label,
                visible: false,
                index: 9,
                data: []
            },
            {
                name: i18n.t('graph.solar_to_battery', { i18nFallback: 'Solar to Battery' }),
                type: "column",
                pointPlacement: "between",
                id: "solar_to_battery",
                color: EssGraphService.getColumnColor(colors.solarToBattery.value),
                borderColor: EssGraphService.getColumnColor(colors.solarToBattery.value),
                colorName: colors.solarToBattery.label,
                visible: false,
                index: 8,
                data: []
            },
            {
                name: i18n.t('graph.solar_to_consumption', { i18nFallback: 'Solar to Consumption' }),
                type: "column",
                pointPlacement: "between",
                id: "solar_to_consumption",
                color: EssGraphService.getColumnColor(colors.solarToConsumption.value),
                borderColor: EssGraphService.getColumnColor(colors.solarToConsumption.value),
                colorName: colors.solarToConsumption.label,
                visible: false,
                index: 7,
                data: []
            },
            {
                name: i18n.t('graph.battery_to_consumption', { i18nFallback: 'Battery to Consumption' }),
                type: "column",
                pointPlacement: "between",
                id: "battery_to_consumption",
                color: EssGraphService.getColumnColor(colors.batteryToConsumption.value),
                borderColor: EssGraphService.getColumnColor(colors.batteryToConsumption.value),
                colorName: colors.batteryToConsumption.label,
                visible: false,
                index: 0,
                data: []
            },
            {
                name: i18n.t('graph.grid_to_consumption', { i18nFallback: 'Grid to Consumption' }),
                type: "column",
                pointPlacement: "between",
                id: "grid_to_consumption",
                color: EssGraphService.getColumnColor(colors.gridToConsumption.value),
                borderColor: EssGraphService.getColumnColor(colors.gridToConsumption.value),
                colorName: colors.gridToConsumption.label,
                visible: false,
                index: 0,
                data: []
            },
            {
                name: i18n.t('graph.grid_to_battery', { i18nFallback: 'Grid to Battery' }),
                type: "column",
                pointPlacement: "between",
                id: "grid_to_battery",
                color: EssGraphService.getColumnColor(colors.gridToBattery.value),
                borderColor: EssGraphService.getColumnColor(colors.gridToBattery.value),
                colorName: colors.gridToBattery.label,
                visible: false,
                index: 0,
                data: []
            },
            {
                name: i18n.t('graph.total_solar', { i18nFallback: 'Total Solar' }),
                type: "line",
                zIndex: -1,
                id: "total_solar",
                color: EssGraphService.getColumnColor(colors.transparent.value),
                borderColor: EssGraphService.getColumnColor(colors.transparent.value),
                visible: false,
                data: []
            },
            {
                name: i18n.t('graph.total_consumption', { i18nFallback: 'Total Consumption' }),
                type: "line",
                zIndex: -1,
                id: "total_consumption",
                color: EssGraphService.getColumnColor(colors.transparent.value),
                borderColor: EssGraphService.getColumnColor(colors.transparent.value),
                visible: false,
                data: []
            },
            /*
                FORECAST
             */
            {
                name: i18n.t('graph.battery_to_grid_fc', { i18nFallback: 'Forecasted Battery to Grid' }),
                type: "column",
                pointPlacement: "between",
                id: "battery_to_grid_fc",
                color: EssGraphService.getColumnColor(colors.batteryToGrid.value, true),
                borderColor: EssGraphService.getColumnColor(colors.batteryToGrid.value),
                className: "battery_to_grid_forecast",
                visible: false,
                index: 10,
                data: []
            },
            {
                name: i18n.t('graph.solar_to_grid_fc', { i18nFallback: 'Forecasted Solar to Grid' }),
                type: "column",
                pointPlacement: "between",
                id: "solar_to_grid_fc",
                color: EssGraphService.getColumnColor(colors.solarToGrid.value, true),
                borderColor: EssGraphService.getColumnColor(colors.solarToGrid.value),
                visible: false,
                index: 9,
                data: []
            },
            {
                name: i18n.t('graph.solar_to_battery_fc', { i18nFallback: 'Forecasted Solar to Battery' }),
                type: "column",
                pointPlacement: "between",
                id: "solar_to_battery_fc",
                color: EssGraphService.getColumnColor(colors.solarToBattery.value, true),
                borderColor: EssGraphService.getColumnColor(colors.solarToBattery.value),
                visible: false,
                index: 8,
                data: []
            },
            {
                name: i18n.t('graph.solar_to_consumption_fc', { i18nFallback: 'Forecasted Solar to Consumption' }),
                type: "column",
                pointPlacement: "between",
                id: "solar_to_consumption_fc",
                color: EssGraphService.getColumnColor(colors.solarToConsumption.value, true),
                borderColor: EssGraphService.getColumnColor(colors.solarToConsumption.value),
                visible: false,
                index: 7,
                data: []
            },
            {
                name: i18n.t('graph.battery_to_consumption_fc', { i18nFallback: 'Forecasted Battery to Consumption' }),
                type: "column",
                pointPlacement: "between",
                id: "battery_to_consumption_fc",
                color: EssGraphService.getColumnColor(colors.batteryToConsumption.value, true),
                borderColor: EssGraphService.getColumnColor(colors.batteryToConsumption.value),
                visible: false,
                index: 0,
                data: []
            },
            {
                name: i18n.t('graph.grid_to_consumption_fc', { i18nFallback: 'Forecasted Grid to Consumption' }),
                type: "column",
                pointPlacement: "between",
                id: "grid_to_consumption_fc",
                color: EssGraphService.getColumnColor(colors.gridToConsumption.value, true),
                borderColor: EssGraphService.getColumnColor(colors.gridToConsumption.value),
                visible: false,
                index: 0,
                data: []
            },
            {
                name: i18n.t('graph.grid_to_battery_fc', { i18nFallback: 'Forecasted Grid to Battery' }),
                type: "column",
                pointPlacement: "between",
                id: "grid_to_battery_fc",
                color: EssGraphService.getColumnColor(colors.gridToBattery.value, true),
                borderColor: EssGraphService.getColumnColor(colors.gridToBattery.value),
                visible: false,
                index: 0,
                data: []
            },

            {
                name: i18n.t('graph.total_solar', { i18nFallback: 'Total Solar' }),
                type: "line",
                zIndex: -1,
                id: "total_solar_fc",
                color: EssGraphService.getColumnColor(colors.transparent.value),
                borderColor: EssGraphService.getColumnColor(colors.transparent.value),
                visible: false,
                data: []
            },
            {
                name: i18n.t('graph.total_consumption', { i18nFallback: 'Total Consumption' }),
                type: "line",
                zIndex: -1,
                id: "total_consumption_fc",
                color: EssGraphService.getColumnColor(colors.transparent.value),
                borderColor: EssGraphService.getColumnColor(colors.transparent.value),
                visible: false,
                data: []
            },
        ]
    }
    );
}
